import React, { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const PALETTE = [
  '#2563eb', '#db2777', '#059669', '#ea580c', '#7c3aed', '#0891b2',
];

export interface ComparisonSeriesMeta {
  key: string;
  label: string;
  source: string;
  measure: string;
  format: string;
  axis?: 'left' | 'right';
}

export interface ComparisonResult {
  kind: 'comparison';
  bucket: string;
  timeRange: string;
  series: ComparisonSeriesMeta[];
  data: Array<Record<string, number | string>>;
}

const fmtDate = (value: string | number) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

export const seriesColor = (index: number) => PALETTE[index % PALETTE.length];

// Pearson correlation over points where both keys are present.
export const pearson = (
  data: Array<Record<string, number | string>>,
  a: string,
  b: string
): number | null => {
  const pairs = data.filter((d) => typeof d[a] === 'number' && typeof d[b] === 'number');
  const n = pairs.length;
  if (n < 3) return null;
  const xs = pairs.map((d) => d[a] as number);
  const ys = pairs.map((d) => d[b] as number);
  const mx = xs.reduce((s, v) => s + v, 0) / n;
  const my = ys.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i += 1) {
    const ax = xs[i] - mx;
    const by = ys[i] - my;
    num += ax * by;
    dx += ax * ax;
    dy += by * by;
  }
  if (dx === 0 || dy === 0) return null;
  return num / Math.sqrt(dx * dy);
};

const correlationStrength = (r: number) => {
  const a = Math.abs(r);
  const dir = r >= 0 ? 'positive' : 'negative';
  if (a >= 0.7) return `strong ${dir}`;
  if (a >= 0.4) return `moderate ${dir}`;
  if (a >= 0.2) return `weak ${dir}`;
  return 'little to no';
};

const MultiSeriesChart: React.FC<{
  result: ComparisonResult;
  chartType: string;
  scaleMode: 'dual' | 'normalized';
  height?: number;
}> = ({ result, chartType, scaleMode, height = 320 }) => {
  const { series, data } = result;

  const plotData = useMemo(() => {
    if (scaleMode !== 'normalized') return data;
    const ranges: Record<string, { min: number; max: number }> = {};
    series.forEach((s) => {
      const vals = data.map((d) => d[s.key]).filter((v): v is number => typeof v === 'number');
      ranges[s.key] = { min: Math.min(...vals), max: Math.max(...vals) };
    });
    return data.map((d) => {
      const out: Record<string, number | string> = { date: d.date };
      series.forEach((s) => {
        const v = d[s.key];
        if (typeof v === 'number') {
          const { min, max } = ranges[s.key];
          out[s.key] = max > min ? ((v - min) / (max - min)) * 100 : 50;
          out[`${s.key}_raw`] = v;
        }
      });
      return out;
    });
  }, [data, series, scaleMode]);

  if (!data.length) {
    return <div className="alert alert-info mb-0">No overlapping data for these series.</div>;
  }

  const normalized = scaleMode === 'normalized';
  const hasRight = !normalized && series.some((s) => s.axis === 'right');

  const tooltipFormatter = (value: number, name: string, entry: { dataKey?: string | number; payload?: Record<string, number> }) => {
    if (normalized) {
      const raw = entry?.payload?.[`${String(entry.dataKey)}_raw`];
      return [raw != null ? Number(raw).toLocaleString() : `${value.toFixed(0)}`, name];
    }
    return [Number(value).toLocaleString(), name];
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={plotData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={24} />
        <YAxis
          yAxisId="left"
          allowDecimals={false}
          domain={normalized ? [0, 100] : ['auto', 'auto']}
          tickFormatter={normalized ? (v: number) => `${v}` : undefined}
        />
        {hasRight && <YAxis yAxisId="right" orientation="right" allowDecimals={false} />}
        <Tooltip labelFormatter={fmtDate} formatter={tooltipFormatter} />
        <Legend />
        {series.map((s, i) => {
          const color = seriesColor(i);
          const yAxisId = normalized ? 'left' : s.axis === 'right' ? 'right' : 'left';
          if (chartType === 'bar') {
            return <Bar key={s.key} yAxisId={yAxisId} dataKey={s.key} name={s.label} fill={color} />;
          }
          if (chartType === 'area') {
            return (
              <Area
                key={s.key}
                yAxisId={yAxisId}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={color}
                fill={color}
                fillOpacity={0.25}
                connectNulls
              />
            );
          }
          return (
            <Line
              key={s.key}
              yAxisId={yAxisId}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={color}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// Pearson readout + scatter, shown when exactly two series are compared.
export const CorrelationPanel: React.FC<{ result: ComparisonResult }> = ({ result }) => {
  const { series, data } = result;
  if (series.length !== 2) return null;
  const [a, b] = series;
  const r = pearson(data, a.key, b.key);
  const scatter = data
    .filter((d) => typeof d[a.key] === 'number' && typeof d[b.key] === 'number')
    .map((d) => ({ x: d[a.key] as number, y: d[b.key] as number }));

  return (
    <div className="mt-3">
      <div className="d-flex align-items-baseline gap-2 mb-2">
        <strong>Correlation (Pearson r):</strong>
        {r == null ? (
          <span className="text-muted">not enough overlapping points</span>
        ) : (
          <span>
            <span className="badge bg-primary">{r.toFixed(2)}</span>{' '}
            <span className="text-muted">— {correlationStrength(r)}</span>
          </span>
        )}
      </div>
      {scatter.length >= 3 && (
        <ResponsiveContainer width="100%" height={240}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" dataKey="x" name={a.label} tick={{ fontSize: 11 }}>
            </XAxis>
            <YAxis type="number" dataKey="y" name={b.label} tick={{ fontSize: 11 }} />
            <ZAxis range={[60, 60]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              formatter={(value: number, name: string) => [Number(value).toLocaleString(), name === 'x' ? a.label : b.label]}
            />
            <Scatter data={scatter} fill={seriesColor(0)} />
          </ScatterChart>
        </ResponsiveContainer>
      )}
      <div className="small text-muted">
        {a.label} (x) vs {b.label} (y), one point per {result.bucket}.
      </div>
    </div>
  );
};

export default MultiSeriesChart;
